import {
  Descriptions,
  Header,
  Icon,
  IconChevronDoubleRight,
  Space,
  Text,
  TransparentIconButton,
} from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { useProcurifyIntl } from '@webapp/hooks'
import { CatalogItemDetailsSparkle } from './CatalogItemDetailsSparkle'
import messages from './messages'
import { CatalogItemDetailsAnalysisStyled } from './styles/CatalogItemDetailsAnalysisStyled'
import { CatalogItemDetailsAnalysisTextStyled } from './styles/CatalogItemDetailsAnalysisTextStyled'
import { CatalogItemDetailsBodyStyled } from './styles/CatalogItemDetailsBodyStyled'
import { CatalogItemDetailsDescriptionsStyled } from './styles/CatalogItemDetailsDescriptionsStyled'
import { CatalogItemDetailsFooterStyled } from './styles/CatalogItemDetailsFooterStyled'
import { CatalogItemDetailsHeaderStyled } from './styles/CatalogItemDetailsHeaderStyled'
import { CatalogItemDetailsImageStyled } from './styles/CatalogItemDetailsImageStyled'
import { CatalogItemDetailsImageWrapperStyled } from './styles/CatalogItemDetailsImageWrapperStyled'
import { CatalogItemDetailsLayoutStyled } from './styles/CatalogItemDetailsLayoutStyled'
import { CatalogItemDetailsPriceStyled } from './styles/CatalogItemDetailsPriceStyled'
import { CatalogItemDetailsSectionStyled } from './styles/CatalogItemDetailsSectionStyled'
import { CatalogItemDetailsSourceRowStyled } from './styles/CatalogItemDetailsSourceRowStyled'
import { CatalogItemDetailsSourceStyled } from './styles/CatalogItemDetailsSourceStyled'
import { CatalogItemDetailsCart } from '../CatalogItemDetailsCart'
import { type ICatalogItemTagProps } from '../CatalogItemTag'

export interface ICatalogItemDetail {
  label: string
  value: string
}

export interface ICatalogItemDetailsProps {
  img?: string
  title: string
  sku: string
  tag: ICatalogItemTagProps
  price: number
  currency: CurrencyName
  agentAnalysis: string
  catalogDetails: ICatalogItemDetail[]
  quantity: number
  estimatedTotal: number
  onQuantityChange: (quantity: number) => void
  onAddToRequest: () => void
  onCollapse: () => void
}

export const CatalogItemDetails = ({
  img,
  title,
  sku,
  tag,
  price,
  currency,
  agentAnalysis,
  catalogDetails,
  quantity,
  estimatedTotal,
  onQuantityChange,
  onAddToRequest,
  onCollapse,
}: ICatalogItemDetailsProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()

  return (
    <CatalogItemDetailsLayoutStyled>
      <CatalogItemDetailsHeaderStyled>
        <TransparentIconButton
          iconComponent={<IconChevronDoubleRight size={24} block aria-hidden />}
          aria-label={intl.formatMessage(messages.collapseAriaLabel)}
          analyticsId='catalog-item-details-collapse-button'
          onClick={onCollapse}
        />
      </CatalogItemDetailsHeaderStyled>

      <CatalogItemDetailsBodyStyled>
        <div>
          <Space direction='vertical' size='medium'>
            {img && (
              <CatalogItemDetailsImageWrapperStyled>
                <CatalogItemDetailsImageStyled src={formatMedia(img)} alt='' />
              </CatalogItemDetailsImageWrapperStyled>
            )}
            <Space direction='vertical' size='xsmall'>
              <Header as='h4'>
                <span className='fs-mask'>{title}</span>
              </Header>
              <Text color='darkGrey' className='fs-mask'>
                {intl.formatMessage(messages.sku, { sku })}
              </Text>
            </Space>
            <CatalogItemDetailsSourceRowStyled>
              <CatalogItemDetailsSourceStyled>
                <Icon icon={tag.icon} size={24} aria-hidden />
                <Text>{tag.text}</Text>
              </CatalogItemDetailsSourceStyled>
              <CatalogItemDetailsPriceStyled className='fs-mask'>
                <Price inline price={price} currency={currency} />
              </CatalogItemDetailsPriceStyled>
            </CatalogItemDetailsSourceRowStyled>
          </Space>

          <CatalogItemDetailsSectionStyled>
            <Space direction='vertical' size='medium'>
              <Space direction='horizontal' size='small' align='center'>
                <CatalogItemDetailsSparkle />
                <Header as='h5'>
                  {intl.formatMessage(messages.agentAnalysisTitle)}
                </Header>
              </Space>
              <CatalogItemDetailsAnalysisStyled>
                <CatalogItemDetailsAnalysisTextStyled className='fs-mask'>
                  {agentAnalysis}
                </CatalogItemDetailsAnalysisTextStyled>
              </CatalogItemDetailsAnalysisStyled>
            </Space>
          </CatalogItemDetailsSectionStyled>

          <CatalogItemDetailsSectionStyled>
            <Space direction='vertical' size='small'>
              <Header as='h5'>
                {intl.formatMessage(messages.catalogDetailsTitle)}
              </Header>
              <CatalogItemDetailsDescriptionsStyled>
                <Descriptions valueWidth='fill'>
                  {catalogDetails.map((detail, index) => (
                    <Descriptions.Item
                      key={`${index}-${detail.label}`}
                      label={detail.label}
                    >
                      {detail.value}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </CatalogItemDetailsDescriptionsStyled>
            </Space>
          </CatalogItemDetailsSectionStyled>
        </div>
      </CatalogItemDetailsBodyStyled>

      <CatalogItemDetailsFooterStyled>
        <CatalogItemDetailsCart
          quantity={quantity}
          estimatedTotal={estimatedTotal}
          currency={currency}
          onQuantityChange={onQuantityChange}
          onAddToRequest={onAddToRequest}
        />
      </CatalogItemDetailsFooterStyled>
    </CatalogItemDetailsLayoutStyled>
  )
}
